import { useEffect, useState, useContext, useCallback } from "react"
import { TabView, TabPanel } from "primereact/tabview"
import ROUTES from "../../Routes/routesContants"

//Tasks modules
import TasksCreated from "./TasksCreated"
import TaskAssigned from "./TaskAssigned"

import TaskCreateForm from "./TaskCreateForm"

//GraphQL
import { useLazyQuery, useQuery } from "@apollo/client"
import { GET_USERS, GET_USERS_FROM_LIST } from "../taskQueries"
import { Dialog } from "primereact/dialog"

// Context
import { UserContext } from "../../../context/userContext"

//style
import taskStyle from "../../../styles/tasks/TasksForm.module.css"
import { useNavigate } from "react-router"

export default function TasksForm({
  users,
  activeKey,
  activeSubKey,
  onSetUsers,
  skipUserRefresh,
  onShowError,
  onSetSkipUserRefresh,
  onHideDialog,
}) {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const staff_id = userCtx.session.user.staff_id
  const organization_id = userCtx.session.user.organization_id

  const [loading, setLoading] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [refreshTasks, setRefreshTasks] = useState(new Date().getTime())
  const [refreshUsers, setRefreshUsers] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editTask, setEditTask] = useState(null)
  const [listUsers, setListUsers] = useState([])

  const setSkipUserRefresh = useCallback(
    (refresh) => {
      onSetSkipUserRefresh(refresh)
    },
    [onSetSkipUserRefresh]
  )

  const { refetch } = useQuery(GET_USERS, {
    fetchPolicy: "network-only",
    skip: skipUserRefresh,
    variables: {
      staff_id,
      organization_id,
    },
    onError: (e) => {
      console.log(e)
      onShowError(
        "Unable to retrieve user information, please refer to the console for more information"
      )
      onSetUsers([])
      onSetSkipUserRefresh(true)
      setRefreshUsers(false)
      setLoading(false)
    },
    onCompleted: (data) => {
      const users = data.users.map((x) => {
        return {
          id: x.staff_id,
          staff_id: x.staff_id,
          participant_id: undefined,
          alias: x.staff_alias,
          status: x.staff_user_status,
          email: x.staff_email_address,
          optionName: `${x.staff_name_first} ${x.staff_name_last}`,
          name2: `${x.staff_name_first} ${x.staff_name_last}`,
          userRole: "Staff",
        }
      })

      const participants = data.participants.map((x) => {
        return {
          id: x.participant_id,
          staff_id: undefined,
          participant_id: x.participant_id,
          alias: x.participant_alias,
          status: x.participant_membership_status,
          email: x.participant_email_account,
          optionName: `${x.participant_name_first} ${x.participant_name_last}`,
          name2: `${x.participant_name_first} ${x.participant_name_last}`,
          userRole: "Participants",
        }
      })

      const combined = [...users, ...participants].sort(function (a, b) {
        let x = a.optionName.toLowerCase()
        let y = b.optionName.toLowerCase()
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
        return 0
      })

      onSetUsers(combined)
      onSetSkipUserRefresh(true)
      setRefreshUsers(false)
      setLoading(false)
    },
  })

  useEffect(() => {
    setRefreshUsers(true)
  }, [userCtx.session.user.organization_id])

  const [loadFromList] = useLazyQuery(GET_USERS_FROM_LIST, {
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    setLoading(!skipUserRefresh)
  }, [skipUserRefresh])

  useEffect(() => {
    if (refreshUsers) {
      if (skipUserRefresh) {
        setSkipUserRefresh(false)
        refetch()
        setLoading(true)
      } else {
        setRefreshUsers(false)
      }
    }
  }, [refreshUsers, skipUserRefresh, setSkipUserRefresh, refetch])

  useEffect(() => {
    setSelectedIndex(activeKey === "assigned-tasks" ? 0 : 1)
  }, [activeKey])

  async function checkUserCache(userIds) {
    try {
      let queryUserIds = userIds

      if (!!listUsers) {
        const missingUsers = userIds.filter(
          (userId) =>
            users.findIndex((y) => y.id === userId) < 0 &&
            listUsers.findIndex((y) => y.staff_id === userId) < 0
        )
        if (missingUsers.length > 0) {
          queryUserIds = userIds
          setLoading(true)
          loadFromList({ list: missingUsers })
        } else {
          queryUserIds = undefined
        }
      } else {
        loadFromList(userIds)
      }

      if (queryUserIds) {
        setLoading(true)
        let result = await loadFromList({ list: queryUserIds })

        if (result.data.users !== null) {
          let newUsers = result.data.users
            .filter((x) => x !== null)
            .map((x) => {
              return {
                id: x.staff_id,
                staff_id: x.staff_id,
                participant_id: undefined,
                alias: x.staff_alias,
                status: x.staff_user_status,
                email: x.staff_email_address,
                name2: `${x.staff_name_first} ${x.staff_name_last}`,
                userRole: x.staff_role_name,
              }
            })
          setListUsers([...listUsers, ...newUsers])
        }
        setLoading(false)
      }
    } catch (error) {
      console.log(error)
      onShowError(
        "Unable to retrieve user information, please refer to the console for more information"
      )
      setLoading(false)
    }
  }

  function getUser(id) {
    if (users) {
      const usr = users?.filter((x) => x.id === id)

      if (usr.length > 0) {
        return usr[0]
      } else {
        const listUsr = listUsers?.filter((x) => x.staff_id === id)

        if (listUsr.length > 0) {
          return listUsr[0]
        } else {
          return null
        }
      }
    } else {
      return null
    }
  }

  async function handleAction(actionUrl, payload, task) {
    switch (actionUrl) {
      case "#assigned-tasks":
        setSelectedIndex(0)
        break

      case "#created-tasks-pending":
        setSelectedIndex(1)
        break
      case "#gpra-followUp-available":
        const fixedPayload = payload.replace(/["]+/g, "")
        await userCtx.changeParticipant(fixedPayload) // changeParcipant receive participant_id or null

        navigate(ROUTES.PARTICIPANT_GPRA_FORMS, {
          replace: true,
        })
        onHideDialog()
        break
      case "#ScheduledAssessment":
        userCtx.setNotificationAction({ actionUrl, payload })

        break
      default:
        window.location.href = actionUrl
    }
  }

  function getActionButton(label, url, payload, gridClass, task) {
    return (
      <>
        {url ? (
          <div className={gridClass}>
            <button
              className={`btn ${taskStyle.taskActionButton}`}
              onClick={() => {
                handleAction(url, payload, task)
              }}
            >
              {label}
            </button>
          </div>
        ) : null}
      </>
    )
  }

  return (
    <div style={{ position: "relative" }}>
      <Dialog
        header={!!editTask ? "Update Task" : "New Task"}
        visible={showCreateForm}
        onHide={() => {
          setShowCreateForm(false)
        }}
        style={{ width: "535px", height: "900px" }}
        //contentStyle={{ padding: 0 }}
      >
        <TaskCreateForm
          users={users}
          onSetShowForm={setShowCreateForm}
          task={editTask}
          onSetTask={setEditTask}
          onShowError={onShowError}
          onRefreshTasks={setRefreshTasks}
          getUser={getUser}
        />
      </Dialog>
      <TabView
        activeIndex={selectedIndex}
        onTabChange={(e) => setSelectedIndex(e.index)}
        id="SearchMe"
      >
        <TabPanel header="Asigned to me">
          <TaskAssigned
            parentLoading={loading}
            refreshTasks={refreshTasks}
            activeOptionPanel={activeSubKey}
            onRefreshTasks={setRefreshTasks}
            users={users}
            onShowError={onShowError}
            checkUserCache={(staff_ids) => {
              checkUserCache(staff_ids)
            }}
            getUser={getUser}
            getActionButton={getActionButton}
          />
        </TabPanel>
        {userCtx.session.user.staff_role < 8 ? (
          <TabPanel header="Created by me">
            <TasksCreated
              parentLoading={loading}
              refreshTasks={refreshTasks}
              onRefreshTasks={setRefreshTasks}
              activeOptionPanel={activeSubKey}
              users={users}
              onSetShowCreateForm={setShowCreateForm}
              onSetEditTask={setEditTask}
              onShowError={onShowError}
              checkUserCache={(staff_ids) => {
                checkUserCache(staff_ids)
              }}
              getUser={getUser}
              getActionButton={getActionButton}
            />
          </TabPanel>
        ) : null}
      </TabView>
      <button
        type="button"
        className="btn"
        style={{
          position: "absolute",
          right: 22,
          top: 5,
          width: 40,
          height: 33,
          padding: "4px 9px",
        }}
        onClick={(e) => {
          setRefreshTasks(new Date().getTime())
          setRefreshUsers(true)
        }}
      >
        <span className={`material-icons`}> {"autorenew"} </span>
      </button>
      <button
        type="button"
        className="btn"
        style={{
          position: "absolute",
          right: 72,
          top: 5,
          width: 40,
          height: 33,
          display: "flex",
          padding: "4px 9px",
        }}
        onClick={(e) => {
          //setSelectedIndex(1)
          setShowCreateForm(true)
          setEditTask(null)
        }}
      >
        <span className={`material-icons`}> {"add"} </span>
      </button>
    </div>
  )
}
